import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { PRO_PRODUCTIONS_PER_YEAR } from "@produce/shared";

interface UsageSectionProps {
  planTier?: string;
  planType?: string;
  planStatus?: string;
  trialEndsAt?: string;
  isLoading?: boolean;
}

export function UsageSection({
  planTier,
  planType,
  planStatus,
  trialEndsAt,
  isLoading: isSubscriptionLoading,
}: UsageSectionProps) {
  const { userId } = useAuth();

  // PRO's limit is 10 published productions per calendar year, enforced by
  // the database (check_can_create_production, migration 20260402160000).
  // Ask that same function, so this page and the limit cannot disagree.
  const { data: yearUsage, isLoading: usageLoading } = useQuery({
    queryKey: ["production-year-usage", userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase.rpc("check_can_create_production", {
        p_user_id: userId,
      });
      if (error) return null;
      return data?.[0] ?? null;
    },
    enabled: planTier === "pro" && !!userId,
  });

  const isLoading = isSubscriptionLoading || usageLoading;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Usage</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </CardContent>
      </Card>
    );
  }

  // Calculate remaining trial days
  const getTrialDaysRemaining = () => {
    if (!trialEndsAt) return null;
    const endDate = new Date(trialEndsAt);
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const trialDaysRemaining = getTrialDaysRemaining();
  const isTrial = planStatus === "trial";

  // Determine display text for plan type
  const getDisplayPlanType = () => {
    if (isTrial) {
      return trialDaysRemaining !== null ? `Trial - ${trialDaysRemaining} days left` : "Trial";
    }
    return planType === "yearly" ? "Yearly" : "Monthly";
  };

  const displayPlanType = getDisplayPlanType();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Usage</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Show trial already used message for returning users */}
        {planStatus === "expired" && (
          <div className="mb-4 rounded-lg border border-destructive/20 bg-destructive/10 p-4">
            <p className="text-sm font-medium text-destructive">
              Your free trial has already been used
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Upgrade to PRO or STUDIO to continue using PROduce with full features.
            </p>
          </div>
        )}

        <div>
          <p className="mb-1 text-sm text-muted-foreground">Current plan</p>
          <p className="font-medium">
            {planStatus === "expired" ? (
              "No active plan"
            ) : (
              <>
                PROducer {planTier?.toUpperCase() || "LITE"} ({displayPlanType})
              </>
            )}
          </p>
        </div>

        {planTier === "lite" && (
          <>
            <div>
              <p className="mb-1 text-sm text-muted-foreground">Crew members</p>
              <p className="font-medium">1 / 1 (only you)</p>
            </div>
            <div>
              <p className="mb-1 text-sm text-muted-foreground">Productions</p>
              <p className="font-medium">Unlimited</p>
            </div>
          </>
        )}

        {planTier === "pro" && (
          <>
            {/* Paid PRO only; trials show "Unlimited during trial" below. */}
            {!isTrial && yearUsage && (
              <div>
                <p className="mb-2 text-sm text-muted-foreground">Productions this year</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>
                      {yearUsage.productions_this_year} / {PRO_PRODUCTIONS_PER_YEAR}
                    </span>
                    <span className="text-muted-foreground">
                      {Math.max(PRO_PRODUCTIONS_PER_YEAR - yearUsage.productions_this_year, 0)}{" "}
                      remaining
                    </span>
                  </div>
                  <Progress
                    value={Math.min(
                      (yearUsage.productions_this_year / PRO_PRODUCTIONS_PER_YEAR) * 100,
                      100,
                    )}
                  />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">Resets on 1 January.</p>
              </div>
            )}
            {isTrial && (
              <div>
                <p className="mb-1 text-sm text-muted-foreground">Productions</p>
                <p className="font-medium">Unlimited during trial</p>
              </div>
            )}
            <div>
              <p className="mb-1 text-sm text-muted-foreground">Crew members per production</p>
              <p className="font-medium">Up to 10</p>
            </div>
          </>
        )}

        {planTier === "studio" && (
          <>
            <div>
              <p className="mb-1 text-sm text-muted-foreground">Productions</p>
              <p className="font-medium">Unlimited</p>
            </div>
            <div>
              <p className="mb-1 text-sm text-muted-foreground">Crew members</p>
              <p className="font-medium">Unlimited</p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
